import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { lessonsAPI, jobApiError } from '../services/api';
import { FiX, FiAlertCircle, FiPlus, FiRefreshCw, FiCopy } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

// Every time is stored in UTC and shown here in the viewer's own zone.
const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
const fmtWhen = (iso) => (iso ? `${fmtDay(iso)}, ${fmtTime(iso)}` : '—');
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toLocalInput = (iso) => { const d = new Date(iso); return `${isoDate(d)}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const fromLocalInput = (v) => new Date(v).toISOString();
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const LESSON_COLOR = { SCHEDULED: '#1d4ed8', HELD: '#15803d', CANCELLED: '#64748b', NEEDS_RESCHEDULE: '#b45309' };
const KIND_LABEL = { ONE_TO_ONE: 'One-to-one', GROUP: 'Group' };
const row = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const muted = { color: '#64748b', fontSize: 12 };

const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);

const Modal = ({ title, onClose, children, actions, wide }) => (
  <div className="modal-overlay">
    <div className="modal-content" style={{ maxWidth: wide ? 860 : 560 }}>
      <div className="modal-header"><h2>{title}</h2><button className="modal-close" onClick={onClose}><FiX /></button></div>
      <div className="modal-form">{children}</div>
      <div className="modal-actions">{actions}<button className="btn-secondary" onClick={onClose}>Close</button></div>
    </div>
  </div>
);

const Left = ({ n }) => (
  <span className="status-pill" style={{ color: n <= 0 ? '#b91c1c' : n <= 2 ? '#b45309' : '#15803d' }}>
    {n <= 0 ? 'No lessons left' : `${n} lesson${n === 1 ? '' : 's'} left`}
  </span>
);

// ─── One lesson: attendance, what was taught, move, cancel ─────────────────
const LessonModal = ({ id, curriculum, onClose, onChanged }) => {
  const [lesson, setLesson] = useState(null);
  const [rows, setRows] = useState({});
  const [topics, setTopics] = useState([]);
  const [moveTo, setMoveTo] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  const apply = useCallback((data) => {
    setLesson(data);
    setTopics(data.topics_taught || []);
    setMoveTo(toLocalInput(data.starts_at));
    setRows(Object.fromEntries(data.attendance.map((a) => [a.learner_id, {
      status: a.status.startsWith('CANCELLED_') && a.status !== 'CANCELLED_BY_US' ? 'LEARNER_CANCELLED' : a.status,
      informed_at: a.informed_at ? toLocalInput(a.informed_at) : '', note: a.note || '',
    }])));
  }, []);
  useEffect(() => { lessonsAPI.lesson(id).then((r) => apply(r.data)).catch((e) => setErr(jobApiError(e))); }, [id, apply]);

  const act = async (op, body) => {
    try { const r = await lessonsAPI.lessonAction(id, op, body); apply(r.data); setErr(''); onChanged(); } catch (e) { setErr(jobApiError(e)); }
  };
  if (!lesson) return <Modal title="Lesson" onClose={onClose}><Err text={err} />{!err && 'Loading…'}</Modal>;

  const level = curriculum.flatMap((c) => c.levels).find((l) => l.code === lesson.level);
  const open = lesson.status !== 'HELD' && lesson.status !== 'CANCELLED';
  const started = new Date(lesson.starts_at) <= new Date();
  const setRow = (learnerId, k, v) => setRows((m) => ({ ...m, [learnerId]: { ...m[learnerId], [k]: v } }));
  const saveAttendance = () => act('attendance', {
    rows: lesson.attendance.filter((a) => a.status !== 'CANCELLED_BY_US').map((a) => ({
      learner_id: a.learner_id, status: rows[a.learner_id].status, note: rows[a.learner_id].note,
      informed_at: rows[a.learner_id].status === 'LEARNER_CANCELLED' && rows[a.learner_id].informed_at
        ? fromLocalInput(rows[a.learner_id].informed_at) : null,
    })),
  });

  return (
    <Modal wide title={`${lesson.class_name} · ${fmtWhen(lesson.starts_at)}`} onClose={onClose}>
      <div style={muted}>
        <span className="status-pill" style={{ color: LESSON_COLOR[lesson.status] }}>{lesson.status_label}</span>{' '}
        {KIND_LABEL[lesson.class_kind]} · {lesson.teacher} · {lesson.duration_min} min · times shown in {ZONE}
        {lesson.status_reason && <> · {lesson.status_reason}</>}
        {lesson.meeting_url && <> · <a href={lesson.meeting_url} target="_blank" rel="noreferrer">Meeting link</a></>}
      </div>
      <Err text={err} />

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Attendance</span></div>
        {lesson.attendance.length === 0 && <div className="cv-subsection-empty">No learners in this lesson.</div>}
        {lesson.attendance.map((a) => {
          const r = rows[a.learner_id] || {};
          return (
            <div key={a.learner_id} style={row}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ minWidth: 160 }}>{a.name}</strong>
                {a.status === 'CANCELLED_BY_US' ? <span style={muted}>{a.status_label}</span> : (
                  <select value={r.status} disabled={lesson.status === 'CANCELLED'} onChange={(e) => setRow(a.learner_id, 'status', e.target.value)}>
                    <option value="PENDING">Not marked yet</option>
                    <option value="ATTENDED">Attended</option>
                    <option value="NO_SHOW">Did not come</option>
                    <option value="LEARNER_CANCELLED">Learner cancelled</option>
                  </select>
                )}
                {r.status === 'LEARNER_CANCELLED' && (
                  <label style={muted}>told us at{' '}
                    <input type="datetime-local" value={r.informed_at} onChange={(e) => setRow(a.learner_id, 'informed_at', e.target.value)} />
                  </label>
                )}
                <Left n={a.lessons_left} />
                {a.deducts && <span style={muted}>counted</span>}
              </div>
              <input style={{ width: '100%', marginTop: 6 }} placeholder="Note on this learner (optional)" value={r.note || ''}
                onChange={(e) => setRow(a.learner_id, 'note', e.target.value)} />
              {a.status.startsWith('CANCELLED_') && a.status !== 'CANCELLED_BY_US' && <div style={muted}>{a.status_label}</div>}
            </div>
          );
        })}
        {lesson.attendance.length > 0 && lesson.status !== 'CANCELLED' && (
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <span style={{ ...muted, marginRight: 8 }}>Less than 24 hours' notice counts as a lesson used. Leave "told us at" empty for "just now".</span>
            <button className="btn-primary" onClick={saveAttendance}>Save attendance</button>
          </div>
        )}
      </div>

      {lesson.status !== 'CANCELLED' && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>What was taught</span></div>
          {!level && <div className="cv-subsection-empty">Give the class a level to pick sub-topics.</div>}
          {level && level.units.map((u) => (
            <div key={u.id} style={{ fontSize: 13, marginBottom: 4 }}>
              <span style={{ ...muted, display: 'inline-block', minWidth: 190 }}>Unit {u.number}: {u.title}</span>
              {u.topics.map((t) => (
                <label key={t.id} style={{ marginRight: 12 }} title={t.title}>
                  <input type="checkbox" checked={topics.includes(t.id)}
                    onChange={(e) => setTopics((x) => (e.target.checked ? [...x, t.id] : x.filter((i) => i !== t.id)))} /> {t.letter}. {t.title}
                </label>
              ))}
            </div>
          ))}
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <button className="btn-primary" disabled={!started} onClick={() => act('held', { topics })}>
              {lesson.status === 'HELD' ? 'Update what was taught' : 'Close lesson as held'}
            </button>
          </div>
        </div>
      )}

      {open && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Move or cancel</span></div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="datetime-local" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
            <input style={{ flex: 1, minWidth: 160 }} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button className="btn-primary" onClick={() => act('move', { starts_at: fromLocalInput(moveTo), reason })}>Move</button>
            <button className="btn-secondary" onClick={() => act('flag', { reason })}>Needs a new time</button>
            <button className="btn-secondary" onClick={() => window.confirm('Cancel this lesson? Nobody loses a lesson.') && act('cancel', { reason })}>Cancel lesson</button>
          </div>
        </div>
      )}

      {lesson.changes.length > 0 && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>History</span></div>
          {lesson.changes.map((c) => (
            <div key={c.id} style={{ ...muted, padding: '2px 0' }}>
              {fmtWhen(c.at)} · {c.kind_label}{c.to_starts_at && c.kind === 'MOVED' ? ` to ${fmtWhen(c.to_starts_at)}` : ''} · {c.actor || 'system'}{c.reason ? ` · ${c.reason}` : ''}{c.undone ? ' · undone' : ''}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ─── Overview ──────────────────────────────────────────────────────────────
const OverviewTab = ({ onOpenLesson }) => {
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [changes, setChanges] = useState([]);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const [o, l, c] = await Promise.all([lessonsAPI.overview(), lessonsAPI.levelChanges({ status: 'PROPOSED' }), lessonsAPI.changes({ by: 'teachers' })]);
      setData(o.data); setLevels(l.data); setChanges(c.data.slice(0, 15)); setErr('');
    } catch (e) { setErr(jobApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn) => { try { await fn(); load(); } catch (e) { setErr(jobApiError(e)); } };
  if (!data) return <Err text={err} />;
  const cards = [
    ['Active learners', data.active_learners], ['Lessons held this month', data.lessons_held_this_month],
    ['Attendance this month', data.attendance_rate == null ? '—' : `${data.attendance_rate}%`],
    ['Lessons needing a new time', data.needs_reschedule],
  ];
  return (
    <div>
      <Err text={err} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        {cards.map(([label, value]) => (
          <div key={label} className="cv-subsection" style={{ margin: 0 }}>
            <div style={muted}>{label}</div><div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
          </div>
        ))}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Level changes waiting for you ({levels.length})</span></div>
        {levels.length === 0 && <div className="cv-subsection-empty">Nothing waiting.</div>}
        {levels.map((l) => (
          <div key={l.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}><strong>{l.learner}</strong>: {l.from_level || 'no level'} → {l.to_level} <span style={muted}>· {l.proposed_by} · {l.note}</span></div>
            <button className="btn-primary" onClick={() => run(() => lessonsAPI.decideLevel(l.id, 'approve'))}>Approve</button>
            <button className="btn-secondary" onClick={() => run(() => lessonsAPI.decideLevel(l.id, 'reject'))}>Reject</button>
          </div>
        ))}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Learners to follow up</span></div>
        {data.low_balance.length + data.missed_two_in_a_row.length === 0 && <div className="cv-subsection-empty">Nobody is low on lessons or missing lessons.</div>}
        {data.low_balance.map((l) => <div key={`l${l.id}`} style={row}>{l.name} <Left n={l.left} /></div>)}
        {data.missed_two_in_a_row.map((l) => <div key={`m${l.id}`} style={row}>{l.name} <span className="status-pill" style={{ color: '#b91c1c' }}>Missed 2 lessons in a row</span></div>)}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Changes made by teachers</span></div>
        {changes.length === 0 && <div className="cv-subsection-empty">No changes yet.</div>}
        {changes.map((c) => (
          <div key={c.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onOpenLesson(c.lesson_id)}>
              <strong>{c.class_name}</strong> · {c.kind_label}
              {c.kind === 'MOVED' && <> from {fmtWhen(c.from_starts_at)} to {fmtWhen(c.to_starts_at)}</>}
              <span style={muted}> · {c.actor} · {fmtWhen(c.at)}{c.reason ? ` · ${c.reason}` : ''}{c.undone ? ' · undone' : ''}</span>
            </div>
            {c.can_undo && <button className="btn-secondary" onClick={() => run(() => lessonsAPI.undoChange(c.id))}>Undo</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Schedule ──────────────────────────────────────────────────────────────
const ScheduleTab = ({ teachers, onOpenLesson, stamp }) => {
  const today = new Date();
  const [from, setFrom] = useState(isoDate(today));
  const [to, setTo] = useState(isoDate(new Date(today.getTime() + 14 * 86400000)));
  const [teacher, setTeacher] = useState('');
  const [lessons, setLessons] = useState([]);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    // Whole days in the viewer's zone: midnight of `from` up to midnight after `to`.
    const end = new Date(`${to}T00:00`); end.setDate(end.getDate() + 1);
    lessonsAPI.lessons({ from: new Date(`${from}T00:00`).toISOString(), to: end.toISOString(), teacher: teacher || undefined }).then((r) => { setLessons(r.data); setErr(''); }).catch((e) => setErr(jobApiError(e)));
  }, [from, to, teacher]);
  useEffect(() => { load(); }, [load, stamp]);
  const days = lessons.reduce((m, l) => { const k = fmtDay(l.starts_at); (m[k] = m[k] || []).push(l); return m; }, {});
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /> to
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <select value={teacher} onChange={(e) => setTeacher(e.target.value)}>
          <option value="">All teachers</option>{teachers.map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
        </select>
        <button className="btn-secondary" onClick={load}><FiRefreshCw /></button>
        <span style={muted}>Times shown in {ZONE}</span>
      </div>
      <Err text={err} />
      {lessons.length === 0 && !err && <div className="cv-subsection-empty">No lessons in these dates. Open a class and press "Generate lessons".</div>}
      {Object.entries(days).map(([day, items]) => (
        <div key={day} className="cv-subsection">
          <div className="cv-subsection-title"><span>{day}</span></div>
          {items.map((l) => (
            <div key={l.id} style={{ ...row, display: 'flex', gap: 10, cursor: 'pointer', alignItems: 'center' }} onClick={() => onOpenLesson(l.id)}>
              <strong style={{ minWidth: 60 }}>{fmtTime(l.starts_at)}</strong>
              <span style={{ flex: 1 }}>{l.class_name} <span style={muted}>· {l.teacher} · {KIND_LABEL[l.class_kind]}{l.level ? ` · ${l.level}` : ''}</span></span>
              <span className="status-pill" style={{ color: LESSON_COLOR[l.status] }}>{l.status_label}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

// ─── Classes ───────────────────────────────────────────────────────────────
const ClassModal = ({ id, teachers, levels, onClose, onChanged }) => {
  const isNew = id === 'new';
  const [c, setC] = useState(null);
  const [f, setF] = useState({ name: '', kind: 'ONE_TO_ONE', teacher: teachers[0]?.id || '', level: '', meeting_url: '', max_learners: 6, timezone: 'Europe/Istanbul' });
  const [slots, setSlots] = useState([]);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const apply = useCallback((data) => {
    setC(data);
    setF({ name: data.name, kind: data.kind, teacher: data.teacher.id, level: data.level_id || '', meeting_url: data.meeting_url, max_learners: data.max_learners, timezone: data.timezone, status: data.status });
    setSlots(data.slots);
  }, []);
  useEffect(() => { if (!isNew) lessonsAPI.classDetail(id).then((r) => apply(r.data)).catch((e) => setErr(jobApiError(e))); }, [id, isNew, apply]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const run = async (fn, done) => {
    try { const r = await fn(); apply(r.data); setErr(''); setNote(done ? done(r.data) : ''); onChanged(); } catch (e) { setErr(jobApiError(e)); }
  };
  const body = () => ({ ...f, level: f.level || null, max_learners: parseInt(f.max_learners, 10) || 6 });
  const cid = c?.id;

  return (
    <Modal wide title={isNew && !c ? 'New class' : (c?.name || 'Class')} onClose={onClose}>
      <Err text={err} />
      {note && <div style={{ ...muted, color: '#15803d', margin: '6px 0' }}>{note}</div>}
      <div className="form-row">
        <div className="form-group"><label>Class name *</label><input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Tendai 1:1 or A1 evening group" /></div>
        <div className="form-group"><label>Type</label>
          <select value={f.kind} disabled={!!c} onChange={(e) => set('kind', e.target.value)}>
            <option value="ONE_TO_ONE">One-to-one</option><option value="GROUP">Group</option>
          </select></div>
      </div>
      <div className="form-row">
        <div className="form-group"><label>Teacher *</label>
          <select value={f.teacher} onChange={(e) => set('teacher', e.target.value)}>
            {teachers.filter((t) => t.is_active).map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
          </select></div>
        <div className="form-group"><label>Level</label>
          <select value={f.level} onChange={(e) => set('level', e.target.value)}>
            <option value="">Not set yet</option>{levels.map((l) => <option key={l.id} value={l.id}>{l.code}</option>)}
          </select></div>
        {f.kind === 'GROUP' && <div className="form-group"><label>Most learners</label><input type="number" min="2" value={f.max_learners} onChange={(e) => set('max_learners', e.target.value)} /></div>}
      </div>
      <div className="form-group"><label>Google Meet link</label><input value={f.meeting_url} onChange={(e) => set('meeting_url', e.target.value)} placeholder="https://meet.google.com/…" /></div>
      {c && (
        <div className="form-group"><label>Status</label>
          <select value={f.status} onChange={(e) => set('status', e.target.value)}>
            <option value="ACTIVE">Active</option><option value="PAUSED">Paused</option><option value="ENDED">Ended</option>
          </select></div>
      )}
      <div style={{ textAlign: 'right' }}>
        <button className="btn-primary" disabled={!f.name.trim() || !f.teacher}
          onClick={() => run(() => (c ? lessonsAPI.updateClass(cid, body()) : lessonsAPI.createClass(body())), () => 'Saved.')}>
          {c ? 'Save class' : 'Create class'}
        </button>
      </div>

      {c && (
        <>
          <div className="cv-subsection">
            <div className="cv-subsection-title"><span>Weekly times (in {f.timezone} time)</span></div>
            {slots.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <select value={s.weekday} onChange={(e) => setSlots((x) => x.map((y, j) => (j === i ? { ...y, weekday: parseInt(e.target.value, 10) } : y)))}>
                  {WEEKDAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
                </select>
                <input type="time" value={s.start_time} onChange={(e) => setSlots((x) => x.map((y, j) => (j === i ? { ...y, start_time: e.target.value } : y)))} />
                <span style={muted}>60 minutes</span>
                <button className="btn-secondary" onClick={() => setSlots((x) => x.filter((_, j) => j !== i))}>Remove</button>
              </div>
            ))}
            <button className="btn-secondary" onClick={() => setSlots((x) => [...x, { weekday: 0, start_time: '18:00', duration_min: 60 }])}><FiPlus /> Add a time</button>{' '}
            <button className="btn-primary" onClick={() => run(() => lessonsAPI.classAction(cid, 'slots', { slots }), () => 'Weekly times saved.')}>Save times</button>{' '}
            <button className="btn-primary" disabled={c.slots.length === 0}
              onClick={() => run(() => lessonsAPI.classAction(cid, 'generate'), (d) => `${d.created} lesson(s) created for the next 4 weeks.${d.skipped.length ? ` Skipped: ${d.skipped.join('; ')}.` : ''}`)}>
              Generate lessons
            </button>
            <div style={{ ...muted, marginTop: 6 }}>Lessons are created 4 weeks ahead and topped up every night. 2 or 3 times a week is the usual pace.</div>
          </div>
          <div className="cv-subsection">
            <div className="cv-subsection-title"><span>Learners ({c.learner_count} of {c.capacity})</span></div>
            {c.learners.length === 0 && <div className="cv-subsection-empty">Nobody in this class yet.</div>}
            {c.learners.map((l) => (
              <div key={l.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ flex: 1 }}>{l.name} <Left n={l.lessons_left} /></span>
                <button className="btn-secondary" onClick={() => window.confirm(`Take ${l.name} out of this class?`) && run(() => lessonsAPI.classAction(cid, 'unenrol', { learner: l.id }))}>Remove</button>
              </div>
            ))}
            {c.learner_count < c.capacity ? (
              <ClientPicker actionLabel="Add to class" skipLearnerIds={c.learners.map((l) => l.id)}
                onPick={(p) => run(() => lessonsAPI.classAction(cid, 'enrol', { kind: p.kind, client_id: p.client_id }), () => `${p.name} added to the class.`)}
                onNew={async (nf) => {
                  try {
                    const r = await lessonsAPI.createWalkin(nf);
                    await run(() => lessonsAPI.classAction(cid, 'enrol', { learner: r.data.learner.id }), () => `${nf.first_name} added as a learner and put in the class.`);
                  } catch (e) { setErr(jobApiError(e)); throw e; }
                }} />
            ) : <div style={muted}>This class is full.</div>}
          </div>
        </>
      )}
    </Modal>
  );
};

const ClassesTab = ({ teachers, levels, learners, stamp, bump }) => {
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { lessonsAPI.classes().then((r) => { setClasses(r.data); setErr(''); }).catch((e) => setErr(jobApiError(e))); }, [stamp]);
  return (
    <div>
      <Err text={err} />
      <div style={{ textAlign: 'right', marginBottom: 8 }}>
        <button className="btn-primary" disabled={teachers.length === 0} onClick={() => setOpen('new')}><FiPlus /> New class</button>
      </div>
      {teachers.length === 0 && <div className="cv-subsection-empty">Add a teacher first (Teachers tab).</div>}
      {classes.map((c) => (
        <div key={c.id} className="cv-subsection" style={{ cursor: 'pointer' }} onClick={() => setOpen(c.id)}>
          <strong>{c.name}</strong> <span style={muted}>· {c.kind_label} · {c.teacher.display_name}{c.level ? ` · ${c.level}` : ''} · {c.learner_count} of {c.capacity} learner(s)</span>
          {c.status !== 'ACTIVE' && <span className="status-pill" style={{ color: '#64748b', marginLeft: 8 }}>{c.status}</span>}
          <div style={muted}>
            {c.slots.length ? c.slots.map((s) => `${WEEKDAYS[s.weekday]} ${s.start_time}`).join(', ') + ` (${c.timezone})` : 'No weekly times yet'}
            {!c.meeting_url && ' · no meeting link'}
          </div>
        </div>
      ))}
      {open && <ClassModal id={open} teachers={teachers} levels={levels} onClose={() => setOpen(null)} onChanged={bump} />}
    </div>
  );
};

// ─── Learners ──────────────────────────────────────────────────────────────
export const EMPTY_LEARNER = { first_name: '', last_name: '', email: '', phone: '', date_of_birth: '', country: '', guardian_name: '', guardian_phone: '', guardian_email: '', notes: '' };

// The details of a "Learner" client (someone who only takes lessons). Shared by
// the forms here and the CRM's Learners page.
export const LearnerFields = ({ f, set }) => (
  <>
    <div className="form-row">
      <div className="form-group"><label>First name *</label><input value={f.first_name} onChange={(e) => set('first_name', e.target.value)} /></div>
      <div className="form-group"><label>Last name *</label><input value={f.last_name} onChange={(e) => set('last_name', e.target.value)} /></div>
    </div>
    <div className="form-row">
      <div className="form-group"><label>Email *</label><input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
      <div className="form-group"><label>Phone (WhatsApp)</label><input value={f.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="+263…" /></div>
    </div>
    <div className="form-row">
      <div className="form-group"><label>Date of birth</label><input type="date" value={f.date_of_birth || ''} onChange={(e) => set('date_of_birth', e.target.value)} /></div>
      <div className="form-group"><label>Country</label><input value={f.country} onChange={(e) => set('country', e.target.value)} /></div>
    </div>
    <div style={muted}>Parent or guardian (needed if the learner is under 18)</div>
    <div className="form-row">
      <div className="form-group"><label>Name</label><input value={f.guardian_name} onChange={(e) => set('guardian_name', e.target.value)} /></div>
      <div className="form-group"><label>Phone</label><input value={f.guardian_phone} onChange={(e) => set('guardian_phone', e.target.value)} /></div>
      <div className="form-group"><label>Email</label><input type="email" value={f.guardian_email} onChange={(e) => set('guardian_email', e.target.value)} /></div>
    </div>
  </>
);

// One search over everyone in the system: students, job seekers, tourists,
// medical clients and learners. Nobody found: add them as a new Learner.
// While a new learner is typed in: people already in the system with the same
// email, phone or name, so one person is never entered twice.
export const SamePerson = ({ f, onUse, useLabel }) => {
  const [rows, setRows] = useState([]);
  const { first_name: first, last_name: last, email, phone } = f;
  useEffect(() => {
    if (!((first && last) || (email && email.includes('@')) || (phone && phone.replace(/\D/g, '').length >= 7))) { setRows([]); return undefined; }
    const timer = setTimeout(() => lessonsAPI.duplicateCheck({ first_name: first, last_name: last, email, phone })
      .then((r) => setRows(r.data)).catch(() => setRows([])), 400);
    return () => clearTimeout(timer);
  }, [first, last, email, phone]);
  if (!rows.length) return null;
  return (
    <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 10, margin: '8px 0' }}>
      <div style={{ fontWeight: 600, color: '#92400e' }}>Already in the system? Check before adding a new person.</div>
      {rows.map((c) => (
        <div key={`${c.kind}-${c.client_id}`} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}><strong>{c.name}</strong> <span className="cv-id">{c.ref}</span>
            <span style={muted}> · {c.kind_label} · {c.reasons.join(', ')}{c.email ? ` · ${c.email}` : ''}{c.phone ? ` · ${c.phone}` : ''}</span></span>
          {onUse && <button className="btn-primary" onClick={() => onUse(c)}>{useLabel}</button>}
        </div>
      ))}
    </div>
  );
};

const ClientPicker = ({ actionLabel, onPick, onNew, skipLearnerIds = [] }) => {
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(EMPTY_LEARNER);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return undefined; }
    const timer = setTimeout(() => lessonsAPI.clientSearch(q.trim()).then((r) => setFound(r.data)).catch(() => setFound([])), 250);
    return () => clearTimeout(timer);
  }, [q]);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); setQ(''); setFound([]); } catch { /* the caller shows the error */ } finally { setBusy(false); }
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  if (adding) {
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginTop: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>New learner (only taking lessons)</div>
        <LearnerFields f={f} set={set} />
        <SamePerson f={f} useLabel={`Use this person: ${actionLabel}`}
          onUse={(c) => act(async () => { await onPick(c); setF(EMPTY_LEARNER); setAdding(false); })} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" disabled={busy || !f.first_name || !f.last_name || !f.email}
            onClick={() => act(async () => { await onNew(f); setF(EMPTY_LEARNER); setAdding(false); })}>
            {actionLabel === 'Add to class' ? 'Add learner and put in class' : 'Add learner'}
          </button>
          <button className="btn-secondary" onClick={() => setAdding(false)}>Back to search</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <input style={{ width: '100%' }} placeholder="Search students, job seekers, tourists, medical clients and learners: name, email, phone or ID"
        value={q} onChange={(e) => setQ(e.target.value)} />
      {q.trim().length >= 2 && found.length === 0 && <div className="cv-subsection-empty">Nobody found.</div>}
      {found.map((c) => {
        const here = c.learner_id && skipLearnerIds.includes(c.learner_id);
        return (
          <div key={`${c.kind}-${c.client_id}`} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1 }}><strong>{c.name}</strong> <span className="cv-id">{c.ref}</span>
              <span style={muted}> · {c.kind_label}{c.learner_id ? ` · takes lessons${c.level ? ` (${c.level})` : ''}` : ''}{c.email ? ` · ${c.email}` : ''}</span></span>
            {here ? <span style={muted}>Already here</span>
              : <button className="btn-primary" disabled={busy} onClick={() => act(() => onPick(c))}>{actionLabel}</button>}
          </div>
        );
      })}
      <div style={{ ...muted, marginTop: 6 }}>
        Not in the system? <button className="btn-secondary" onClick={() => setAdding(true)}><FiPlus /> Add a new learner</button>
      </div>
    </div>
  );
};

const AddLearnerModal = ({ onClose, onAdded, existingIds }) => {
  const [err, setErr] = useState('');
  const attempt = async (fn) => {
    try { await fn(); setErr(''); onAdded(); onClose(); } catch (e) { setErr(jobApiError(e)); throw e; }
  };
  return (
    <Modal title="Add a learner" onClose={onClose}>
      <Err text={err} />
      <div style={muted}>Find the person among the clients already in the system, or add them as a new learner if they only want lessons.</div>
      <ClientPicker actionLabel="Start lessons" skipLearnerIds={existingIds}
        onPick={(c) => attempt(() => lessonsAPI.startLearner({ kind: c.kind, client_id: c.client_id }))}
        onNew={(nf) => attempt(() => lessonsAPI.createWalkin(nf))} />
    </Modal>
  );
};

// Self-marking tests: send one as a link; the mark lands here once submitted.
const TEST_STATE = { SENT: 'Sent, not opened', OPENED: 'Opened, not submitted', CANCELLED: 'Withdrawn' };
const LearnerTests = ({ id, level }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [rows, setRows] = useState([]);
  const [quiz, setQuiz] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => lessonsAPI.learnerTests(id).then((r) => setRows(r.data)).catch((e) => setErr(jobApiError(e))), [id]);
  useEffect(() => { load(); lessonsAPI.quizzes().then((r) => setQuizzes(r.data)).catch(() => {}); }, [load]);
  const run = async (fn) => { try { await fn(); setErr(''); load(); } catch (e) { setErr(jobApiError(e)); } };
  const sorted = [...quizzes].sort((a, b) => (b.level === level) - (a.level === level));
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Tests</span></div>
      <Err text={err} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <select style={{ flex: 1 }} value={quiz} onChange={(e) => setQuiz(e.target.value)}>
          <option value="">Choose a test to send…</option>
          {sorted.map((q) => <option key={q.id} value={q.id}>{q.level} · {q.title} ({q.question_count} questions)</option>)}
        </select>
        <button className="btn-primary" disabled={!quiz} onClick={() => run(() => lessonsAPI.sendTest(id, parseInt(quiz, 10))).then(() => setQuiz(''))}>Create test link</button>
      </div>
      {rows.length === 0 && <div className="cv-subsection-empty">No tests sent yet.</div>}
      {rows.map((r) => (
        <div key={r.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}><strong>{r.quiz.level} · {r.quiz.title}</strong>
            <span style={muted}> · sent {fmtDay(r.assigned_at)}{r.assigned_by ? ` by ${r.assigned_by}` : ''}{r.submitted_at ? ` · submitted ${fmtDay(r.submitted_at)}` : ''}</span></span>
          {r.state === 'SUBMITTED' ? (
            <span className="status-pill" style={{ color: r.passed ? '#15803d' : '#b91c1c' }}>{r.score}/{r.max_score} · {r.percent}% · {r.passed ? 'Passed' : 'Not yet'}</span>
          ) : (
            <>
              <span style={muted}>{TEST_STATE[r.state]}</span>
              {r.url && <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(r.url)}><FiCopy /> Copy link</button>}
              {r.whatsapp_url && <a className="btn-secondary" href={r.whatsapp_url} target="_blank" rel="noreferrer">WhatsApp</a>}
              {r.url && <button className="btn-secondary" onClick={() => window.confirm('Withdraw this test? The link will stop working.') && run(() => lessonsAPI.cancelTest(r.id))}>Withdraw</button>}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

// "Is this the same person as a client already in the system?" on a Learner
// client, with a one-click link that moves the lesson history to that record.
const DuplicateOf = ({ learnerId, onLinked }) => {
  const [matches, setMatches] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    lessonsAPI.duplicates().then((r) => setMatches(r.data.find((x) => x.learner_id === learnerId)?.matches || [])).catch(() => {});
  }, [learnerId]);
  if (!matches.length) return null;
  const link = async (c) => {
    if (!window.confirm(`Is this learner the same person as ${c.name} (${c.kind_label} ${c.ref})? Their lessons, packages and tests move to that record and this Learner entry is closed.`)) return;
    try { const r = await lessonsAPI.linkClient(learnerId, { kind: c.kind, client_id: c.client_id }); onLinked(r.data); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 10, margin: '10px 0' }}>
      <div style={{ fontWeight: 600, color: '#92400e' }}>This learner may already be in the system</div>
      <Err text={err} />
      {matches.map((c) => (
        <div key={`${c.kind}-${c.client_id}`} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}><strong>{c.name}</strong> <span className="cv-id">{c.ref}</span>
            <span style={muted}> · {c.kind_label} · {c.reasons.join(', ')}</span></span>
          <button className="btn-primary" onClick={() => link(c)}>Same person: link</button>
        </div>
      ))}
    </div>
  );
};

const AssignTeacher = ({ learnerId, teachers, onDone }) => {
  const [teacher, setTeacher] = useState('');
  const [meet, setMeet] = useState('');
  const [err, setErr] = useState('');
  const go = async () => {
    try { const r = await lessonsAPI.assignTeacher(learnerId, { teacher: parseInt(teacher, 10), meeting_url: meet }); setErr(''); setTeacher(''); setMeet(''); onDone(r.data); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div style={{ marginTop: 6 }}>
      <Err text={err} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={teacher} onChange={(e) => setTeacher(e.target.value)}>
          <option value="">Assign a teacher (one-to-one)…</option>
          {teachers.filter((t) => t.is_active).map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
        </select>
        <input style={{ flex: 1, minWidth: 180 }} placeholder="Google Meet link (optional, can be added later)" value={meet} onChange={(e) => setMeet(e.target.value)} />
        <button className="btn-primary" disabled={!teacher} onClick={go}>Assign</button>
      </div>
      <div style={muted}>Creates a one-to-one class with that teacher and puts this learner in it. Set the weekly times in Classes.</div>
    </div>
  );
};

const LearnerModal = ({ id, templates, levels, teachers = [], canSell, onClose, onChanged, onReplaced }) => {
  const [l, setL] = useState(null);
  const [link, setLink] = useState(null);
  const [sale, setSale] = useState({ template: '', amount: '', paid: false, payment_reference: '' });
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => lessonsAPI.learner(id).then((r) => setL(r.data)).catch((e) => setErr(jobApiError(e))), [id]);
  useEffect(() => { load(); lessonsAPI.learnerLink(id).then((r) => setLink(r.data)).catch(() => {}); }, [id, load]);
  const run = async (fn, after) => { try { const r = await fn(); setErr(''); if (after) after(r.data); else setL(r.data); onChanged(); } catch (e) { setErr(jobApiError(e)); } };
  const chosen = templates.find((t) => String(t.id) === String(sale.template));
  if (!l) return <Modal title="Learner" onClose={onClose}><Err text={err} />{!err && 'Loading…'}</Modal>;
  const copy = (text) => { navigator.clipboard?.writeText(text); setNote('Copied.'); };
  return (
    <Modal wide title={`${l.name} · ${l.kind_label}`} onClose={onClose}>
      <Err text={err} />
      {note && <div style={{ ...muted, color: '#15803d' }}>{note}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="cv-id">{l.ref}</span><Left n={l.lessons_left} />
        <span style={muted}>Level: {l.level || 'not set'}{l.progress.total ? ` · ${l.progress.done} of ${l.progress.total} sub-topics` : ''}{l.level_waiting ? ` · ${l.level_waiting} waiting for approval` : ''}</span>
        <span style={muted}>{l.phone} {l.email}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
        <label style={muted}>Status{' '}
          <select value={l.status} onChange={(e) => run(() => lessonsAPI.updateLearner(id, { status: e.target.value }))}>
            <option value="ACTIVE">Active</option><option value="PAUSED">Paused</option><option value="ARCHIVED">Archived</option>
          </select></label>
        <label style={muted}>Lessons a week{' '}
          <select value={l.lessons_per_week} onChange={(e) => run(() => lessonsAPI.updateLearner(id, { lessons_per_week: parseInt(e.target.value, 10) }))}>
            <option value={2}>2</option><option value={3}>3</option>
          </select></label>
        <label style={muted}>Level{' '}
          <select value={l.level_id || ''} disabled={!canSell} onChange={(e) => e.target.value && run(() => lessonsAPI.updateLearner(id, { level: parseInt(e.target.value, 10) }))}>
            <option value="">Not set</option>{levels.map((x) => <option key={x.id} value={x.id}>{x.code}</option>)}
          </select></label>
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Teacher and classes</span></div>
        {l.classes.length === 0 && <div className="cv-subsection-empty">No teacher yet: not in any class.</div>}
        {l.classes.map((c) => (
          <div key={c.id} style={row}><strong>{c.teacher}</strong> <span style={muted}>· {c.name} · {c.kind === 'ONE_TO_ONE' ? 'one-to-one' : 'group'}</span></div>
        ))}
        {canSell && <AssignTeacher learnerId={id} teachers={teachers} onDone={(d) => { setL(d); setNote('Teacher assigned. Set the weekly times in Classes.'); onChanged(); }} />}
      </div>
      {l.kind === 'WALKIN' && canSell && (
        <DuplicateOf learnerId={id} onLinked={(d) => {
          onChanged();
          // Usually the same row, now owned by the real client; after a merge it is that client's row.
          if (d.id === id) { setL(d); setNote(`Linked: ${d.name} is now one person, ${d.kind_label} ${d.ref}.`); } else if (onReplaced) onReplaced(d.id);
        }} />
      )}

      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Packages</span></div>
        <div style={muted}>One-to-one: {l.balance.ONE_TO_ONE.used} used of {l.balance.ONE_TO_ONE.bought} · Group: {l.balance.GROUP.used} used of {l.balance.GROUP.bought}</div>
        {l.packages.length === 0 && <div className="cv-subsection-empty">No packages yet.</div>}
        {l.packages.map((p) => (
          <div key={p.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center', opacity: p.voided ? 0.5 : 1 }}>
            <span style={{ flex: 1 }}>{p.name} · {p.lessons_total} lesson(s) · {p.amount} {p.currency}
              <span style={muted}> · {fmtDay(p.purchased_at)} · {p.sold_by || ''}{p.payment_reference ? ` · ${p.payment_reference}` : ''}</span></span>
            {p.voided ? <span style={muted}>Cancelled</span> : (
              <>
                <span className="status-pill" style={{ color: p.paid ? '#15803d' : '#b45309' }}>{p.paid ? 'Paid' : 'Not paid'}</span>
                {canSell && !p.paid && <button className="btn-secondary" onClick={() => run(() => lessonsAPI.packageAction(p.id, 'paid'))}>Mark paid</button>}
                {canSell && <button className="btn-secondary" onClick={() => window.confirm('Cancel this package? Use this only for a package entered by mistake.') && run(() => lessonsAPI.packageAction(p.id, 'void'))}>Cancel</button>}
              </>
            )}
          </div>
        ))}
        {canSell && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.4fr auto auto', gap: 6, marginTop: 10, alignItems: 'center' }}>
            <select value={sale.template} onChange={(e) => { const t = templates.find((x) => String(x.id) === e.target.value); setSale((s) => ({ ...s, template: e.target.value, amount: t ? t.price : '' })); }}>
              <option value="">Add a package…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <input type="number" min="0" placeholder="Amount" value={sale.amount} onChange={(e) => setSale((s) => ({ ...s, amount: e.target.value }))} />
            <input placeholder="Receipt / invoice no." value={sale.payment_reference} onChange={(e) => setSale((s) => ({ ...s, payment_reference: e.target.value }))} />
            <label style={muted}><input type="checkbox" checked={sale.paid} onChange={(e) => setSale((s) => ({ ...s, paid: e.target.checked }))} /> Paid</label>
            <button className="btn-primary" disabled={!sale.template}
              onClick={() => run(() => lessonsAPI.sellPackage(id, { ...sale, template: parseInt(sale.template, 10), amount: sale.amount === '' ? null : sale.amount, currency: chosen?.currency }), (d) => { setL(d); setSale({ template: '', amount: '', paid: false, payment_reference: '' }); })}>Add</button>
          </div>
        )}
        {chosen && !chosen.price_set && <div style={{ ...muted, color: '#b45309', marginTop: 4 }}>This package has no price in the price book yet. Type the amount, or set the price under Billing Documents.</div>}
        {l.kind === 'STUDENT' && <div style={{ ...muted, marginTop: 6 }}>For a student, issue the invoice and receipt from Billing Documents as usual and put the number here.</div>}
      </div>

      <LearnerTests id={id} level={l.level} />

      {link && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Private lessons page (no login)</span></div>
          <div style={{ fontSize: 12, wordBreak: 'break-all' }}>{link.is_valid ? link.url : 'This link is switched off.'}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {link.is_valid && <button className="btn-secondary" onClick={() => copy(link.url)}><FiCopy /> Copy</button>}
            {link.is_valid && link.whatsapp_url && <a className="btn-secondary" href={link.whatsapp_url} target="_blank" rel="noreferrer">Send by WhatsApp</a>}
            <button className="btn-secondary" onClick={() => run(() => lessonsAPI.learnerLinkOp(id, 'regenerate'), setLink)}>New link</button>
            {link.is_valid && <button className="btn-secondary" onClick={() => run(() => lessonsAPI.learnerLinkOp(id, 'revoke'), setLink)}>Switch off</button>}
          </div>
          <div style={{ ...muted, marginTop: 6 }}>
            {l.has_app_login ? 'This learner also has an app login.' : 'No app login yet.'}
            {l.kind === 'WALKIN' && !l.has_app_login && (
              <> <button className="btn-secondary" onClick={() => run(() => lessonsAPI.walkinAppAccess(l.client_id), (d) => { if (d.link) copy(d.link); setNote(d.link ? 'App set-password link copied. Send it to the learner.' : 'App login created, but no set-password page is configured in Settings.'); load(); })}>Give app access</button></>
            )}
            {l.kind !== 'WALKIN' && !l.has_app_login && ' Give app access from the client’s own record.'}
          </div>
        </div>
      )}
    </Modal>
  );
};

const LearnersTab = ({ learners, templates, levels, teachers, canSell, bump, openFirst }) => {
  const [open, setOpen] = useState(openFirst || null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [dups, setDups] = useState([]);
  useEffect(() => { if (canSell) lessonsAPI.duplicates().then((r) => setDups(r.data)).catch(() => setDups([])); }, [canSell, learners]);
  const shown = learners.filter((l) => `${l.name} ${l.ref}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input style={{ flex: 1 }} placeholder="Search learners" value={q} onChange={(e) => setQ(e.target.value)} />
        {canSell && <button className="btn-primary" onClick={() => setAdding(true)}><FiPlus /> Add a learner</button>}
      </div>
      {dups.length > 0 && (
        <div style={{ border: '1px solid #fcd34d', background: '#fffbeb', borderRadius: 8, padding: 10, marginBottom: 8 }}>
          <strong style={{ color: '#92400e' }}>{dups.length} learner{dups.length === 1 ? '' : 's'} may already be in the system as another client.</strong>
          <span style={muted}> Open each one and link it if it is the same person: </span>
          {dups.map((d) => <button key={d.learner_id} className="btn-secondary" style={{ margin: 2 }} onClick={() => setOpen(d.learner_id)}>{d.name} → {d.matches[0].kind_label}</button>)}
        </div>
      )}
      {shown.length === 0 && <div className="cv-subsection-empty">No learners yet.</div>}
      {shown.map((l) => (
        <div key={l.id} style={{ ...row, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', opacity: l.status === 'ACTIVE' ? 1 : 0.6 }} onClick={() => setOpen(l.id)}>
          <span style={{ flex: 1 }}><strong>{l.name}</strong> <span className="cv-id">{l.ref}</span> <span style={muted}>· {l.kind_label}{l.status !== 'ACTIVE' ? ` · ${l.status.toLowerCase()}` : ''}</span></span>
          <span style={muted}>{l.teachers.length ? `Teacher: ${l.teachers.join(', ')}` : 'No teacher yet'}</span>
          <span style={muted}>{l.level || 'no level'}{l.progress.total ? ` · ${l.progress.done}/${l.progress.total}` : ''}</span>
          <Left n={l.lessons_left} />
        </div>
      ))}
      {open && <LearnerModal key={open} id={open} templates={templates} levels={levels} teachers={teachers} canSell={canSell}
        onClose={() => setOpen(null)} onChanged={bump}
        onReplaced={(nid) => { setOpen(null); setTimeout(() => setOpen(nid), 0); }} />}
      {adding && <AddLearnerModal onClose={() => setAdding(false)} onAdded={bump} existingIds={learners.map((l) => l.id)} />}
    </div>
  );
};

// ─── Teachers and blocked dates ────────────────────────────────────────────
const TeachersTab = ({ teachers, bump }) => {
  const [f, setF] = useState({ display_name: '', email: '', phone: '' });
  const [links, setLinks] = useState({});
  const [hours, setHours] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [b, setB] = useState({ date_from: '', date_to: '', reason: '', teacher: '' });
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const loadBlocked = useCallback(() => lessonsAPI.blockedDates().then((r) => setBlocked(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { loadBlocked(); lessonsAPI.taughtHours().then((r) => setHours(r.data)).catch(() => setHours(null)); }, [loadBlocked]);
  const run = async (fn, after) => { try { const r = await fn(); setErr(''); if (after) after(r.data); bump(); } catch (e) { setErr(jobApiError(e)); } };
  const hoursOf = (id) => hours?.teachers.find((t) => t.id === id)?.hours;
  return (
    <div>
      <Err text={err} />
      {note && <div style={{ ...muted, color: '#15803d' }}>{note}</div>}
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Teachers</span></div>
        {teachers.length === 0 && <div className="cv-subsection-empty">No teachers yet.</div>}
        {teachers.map((t) => (
          <div key={t.id} style={{ ...row, opacity: t.is_active ? 1 : 0.55 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ flex: 1 }}><strong>{t.display_name}</strong> <span style={muted}>· {t.email} · {t.has_password ? (t.last_login ? `last sign-in ${fmtDay(t.last_login)}` : 'password set') : 'has not set a password yet'}{hoursOf(t.id) != null ? ` · ${hoursOf(t.id)} h taught this month` : ''}</span></span>
              <button className="btn-secondary" onClick={() => run(() => lessonsAPI.teacherLink(t.id), (d) => setLinks((m) => ({ ...m, [t.id]: d.setup_link })))}>Set-password link</button>
              <button className="btn-secondary" onClick={() => run(() => lessonsAPI.updateTeacher(t.id, { is_active: !t.is_active }))}>{t.is_active ? 'Switch off' : 'Switch on'}</button>
            </div>
            {links[t.id] && (
              <div style={{ fontSize: 12, wordBreak: 'break-all', marginTop: 4 }}>
                {links[t.id]} <button className="btn-secondary" onClick={() => { navigator.clipboard?.writeText(links[t.id]); setNote('Copied. Send it to the teacher; it works for 72 hours.'); }}><FiCopy /></button>
              </div>
            )}
          </div>
        ))}
        <div style={{ ...muted, marginTop: 10 }}>Add a teacher. They sign in at teachers.visionexploit.com with their own email (not one already used by a staff login).</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1fr auto', gap: 6, marginTop: 6 }}>
          <input placeholder="Full name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} />
          <input type="email" placeholder="Email for the teachers portal" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <button className="btn-primary" disabled={!f.display_name.trim() || !f.email.trim()}
            onClick={() => run(() => lessonsAPI.createTeacher(f), (d) => { setLinks((m) => ({ ...m, [d.id]: d.setup_link })); setF({ display_name: '', email: '', phone: '' }); })}>Add teacher</button>
        </div>
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Holidays and teacher leave</span></div>
        <div style={muted}>Lessons on these dates are flagged for a new time and never cost a learner a lesson.</div>
        {blocked.map((x) => (
          <div key={x.id} style={{ ...row, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1 }}>{x.date_from}{x.date_to !== x.date_from ? ` to ${x.date_to}` : ''} · {x.reason} <span style={muted}>· {x.teacher || 'everyone'}</span></span>
            <button className="btn-secondary" onClick={() => run(() => lessonsAPI.removeBlockedDate(x.id), loadBlocked)}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.6fr 1.2fr auto', gap: 6, marginTop: 8 }}>
          <input type="date" value={b.date_from} onChange={(e) => setB({ ...b, date_from: e.target.value })} />
          <input type="date" value={b.date_to} onChange={(e) => setB({ ...b, date_to: e.target.value })} />
          <input placeholder="Reason (e.g. Republic Day)" value={b.reason} onChange={(e) => setB({ ...b, reason: e.target.value })} />
          <select value={b.teacher} onChange={(e) => setB({ ...b, teacher: e.target.value })}>
            <option value="">Everyone</option>{teachers.map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
          </select>
          <button className="btn-primary" disabled={!b.date_from || !b.reason.trim()}
            onClick={() => run(() => lessonsAPI.addBlockedDate({ ...b, date_to: b.date_to || b.date_from, teacher: b.teacher || null }), (d) => { setNote(`${d.lessons_flagged} lesson(s) flagged for a new time.`); setB({ date_from: '', date_to: '', reason: '', teacher: '' }); loadBlocked(); })}>Block</button>
        </div>
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────
const TABS = [['overview', 'Overview'], ['schedule', 'Schedule'], ['classes', 'Classes'], ['learners', 'Learners'], ['teachers', 'Teachers & holidays']];

const LanguageLessons = () => {
  const openLearner = parseInt(new URLSearchParams(useLocation().search).get('learner'), 10) || null;
  const [tab, setTab] = useState(openLearner ? 'learners' : 'overview');
  const [stamp, setStamp] = useState(0);
  const [teachers, setTeachers] = useState([]);
  const [learners, setLearners] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [lessonId, setLessonId] = useState(null);
  const [financeOnly, setFinanceOnly] = useState(false);
  const [error, setError] = useState('');
  const bump = useCallback(() => setStamp((n) => n + 1), []);

  useEffect(() => {
    (async () => {
      try {
        const [l, p] = await Promise.all([lessonsAPI.learners(), lessonsAPI.packageTemplates()]);
        setLearners(l.data); setTemplates(p.data); setError('');
      } catch (e) {
        setError(e?.response?.status === 403 ? 'Language Lessons is for leadership, admins and consultants.' : jobApiError(e));
        return;
      }
      try {
        const [t, c] = await Promise.all([lessonsAPI.teachers(), lessonsAPI.curriculum()]);
        setTeachers(t.data); setCurriculum(c.data); setFinanceOnly(false);
      } catch (e) {
        // The CFO reads packages and balances only.
        if (e?.response?.status === 403) { setFinanceOnly(true); setTab('learners'); } else setError(jobApiError(e));
      }
    })();
  }, [stamp]);

  const levels = curriculum.flatMap((c) => c.levels);
  const tabs = financeOnly ? TABS.filter(([k]) => k === 'learners') : TABS;
  return (
    <div className="students-page">
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => <button key={k} className={tab === k ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      <Err text={error} />
      {!error && tab === 'overview' && <OverviewTab key={stamp} onOpenLesson={setLessonId} />}
      {!error && tab === 'schedule' && <ScheduleTab teachers={teachers} onOpenLesson={setLessonId} stamp={stamp} />}
      {!error && tab === 'classes' && <ClassesTab teachers={teachers} levels={levels} learners={learners} stamp={stamp} bump={bump} />}
      {!error && tab === 'learners' && <LearnersTab learners={learners} templates={templates} levels={levels} teachers={teachers} canSell={!financeOnly} bump={bump} openFirst={openLearner} />}
      {!error && tab === 'teachers' && <TeachersTab teachers={teachers} bump={bump} />}
      {lessonId && <LessonModal id={lessonId} curriculum={curriculum} onClose={() => setLessonId(null)} onChanged={bump} />}
    </div>
  );
};

export default LanguageLessons;
